import * as PIXI from 'pixi.js';

export type EndingType = 'victory' | 'gameOver';

export interface EndingScreenOptions {
  type: EndingType;
  endingName?: string;
  title?: string;
  caption?: string;
  progress?: number;
  onRestart?: () => void;
  onGallery?: () => void;
  onShare?: () => void;
}

export class EndingScreen {
  private container: PIXI.Container;
  private backgroundSprite: PIXI.Sprite;
  private titleText: PIXI.Text;
  private captionText: PIXI.Text;
  private progressContainer: PIXI.Container;
  private progressLabel: PIXI.Text;
  private progressValue: PIXI.Text;

  private buttonContainer: PIXI.Container;
  private galleryButton: PIXI.Container;
  private restartButton: PIXI.Container;
  private shareButton: PIXI.Container;

  private isVisible: boolean = false;
  private screenWidth: number;
  private screenHeight: number;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create main container
    this.container = new PIXI.Container();
    this.container.visible = false;
    this.container.zIndex = 1000;

    // Background
    this.backgroundSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    this.backgroundSprite.tint = 0x000000;
    this.backgroundSprite.anchor.set(0.5);
    this.container.addChild(this.backgroundSprite);

    // Load texture explicitly
    PIXI.Assets.load('/assets/background/end_screen.png').then((texture) => {
      if (texture) {
        console.log('Ending Screen background loaded successfully');
        this.backgroundSprite.texture = texture;
        this.backgroundSprite.tint = 0xffffff;
        this.updateLayout();
      } else {
        console.error('Failed to load Ending Screen background');
      }
    }).catch(err => {
      console.error('Error loading Ending Screen background:', err);
    });

    // Title: "THE END"
    this.titleText = new PIXI.Text('THE END', {
      fontFamily: '"Erben Gothic", serif',
      fontSize: 100,
      fill: '#D4AF37',
      align: 'center',
      padding: 8,
      dropShadow: {
        color: 0x000000,
        alpha: 0.5,
        blur: 4,
        distance: 2,
        angle: Math.PI / 4,
      },
    });
    this.titleText.resolution = window.devicePixelRatio || 2;
    this.titleText.anchor.set(0.5);
    this.container.addChild(this.titleText);

    // Caption: "You Have Reached Ending [...]"
    this.captionText = new PIXI.Text('', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 32,
      fill: '#D7CDAE',
      align: 'center',
      padding: 4,
      wordWrap: true,
      wordWrapWidth: 600,
    });
    this.captionText.resolution = window.devicePixelRatio || 2;
    this.captionText.anchor.set(0.5);
    this.container.addChild(this.captionText);

    // Progress Container
    this.progressContainer = new PIXI.Container();
    this.container.addChild(this.progressContainer);

    // "Progress" Label (Beige)
    this.progressLabel = new PIXI.Text('Progress ', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: '#D7CDAE',
      align: 'center',
      padding: 4,
    });
    this.progressLabel.resolution = window.devicePixelRatio || 2;
    this.progressLabel.anchor.set(0, 0.5);
    this.progressContainer.addChild(this.progressLabel);

    // Percentage Value (Yellow)
    this.progressValue = new PIXI.Text('0%', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: '#D4AF37',
      align: 'center',
      padding: 4,
    });
    this.progressValue.resolution = window.devicePixelRatio || 2;
    this.progressValue.anchor.set(0, 0.5);
    this.progressContainer.addChild(this.progressValue);

    // Buttons container
    this.buttonContainer = new PIXI.Container();
    this.container.addChild(this.buttonContainer);

    this.galleryButton = this.createButton('/assets/ui/gallery.png', 'Gallery', '/assets/ui/icon-gallery.png');
    this.restartButton = this.createButton('/assets/ui/try-again.png', 'Try Again', '/assets/ui/icon-try-again.png');
    this.shareButton = this.createButton('/assets/ui/share.png', 'Share', '/assets/ui/icon-share.png');

    this.buttonContainer.addChild(this.galleryButton);
    this.buttonContainer.addChild(this.restartButton);
    this.buttonContainer.addChild(this.shareButton);

    // Position elements
    this.updateLayout();
  }

  private getScaleFactor(): number {
    const referenceShortSide = 600; // "comfortable" baseline (px)
    const shortSide = Math.min(this.screenWidth, this.screenHeight);
    const factor = Math.min(1, shortSide / referenceShortSide);
    return Math.max(0.45, factor); // never go below 45 %
  }

  private createButton(imagePath: string, label: string, iconPath: string): PIXI.Container {
    const container = new PIXI.Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';

    const sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    sprite.anchor.set(0.5);
    sprite.scale.set(0.4);
    container.addChild(sprite);

    // Load button texture explicitly
    PIXI.Assets.load(imagePath).then((texture) => {
      if (texture) {
        sprite.texture = texture;
        this.updateLayout();
      }
    }).catch(err => {
      console.error(`Error loading button texture ${imagePath}:`, err);
    });

    // Content Container
    const content = new PIXI.Container();
    container.addChild(content);

    // Icon Sprite
    const iconSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    iconSprite.anchor.set(0, 0.5);
    content.addChild(iconSprite);

    // Load icon texture
    PIXI.Assets.load(iconPath).then((texture) => {
      if (texture) {
        iconSprite.texture = texture;
            // Scale icon to fit height of approx 32px
        const targetHeight = 32;
        const scale = targetHeight / texture.height;
        iconSprite.scale.set(scale);
        this.updateLayout();
      }
    });

    // Label
    const text = new PIXI.Text(label, {
      fontFamily: 'Rosarivo, serif',
      fontSize: 24,
      fill: 0xFFFFFF,
      fontWeight: 'bold',
      align: 'center',
      padding: 4,
    });
    text.resolution = window.devicePixelRatio || 2;
    text.anchor.set(0, 0.5);

    // Position
    const iconWidth = 32; // Assumed width
    const spacing = 15;
    text.x = iconWidth + spacing;
    content.addChild(text);

    // Center content
    // Pivot at center of the group
    const totalWidth = iconWidth + spacing + text.width;
    content.pivot.set(totalWidth / 2, 0);
    
    content.x = 0;
    content.y = 0;

    // Hover effects
    const resetBtn = () => {
      container.scale.set((container as any)._baseScale ?? 1);
      sprite.tint = 0xFFFFFF;
    };
    container.on('pointerover', () => {
      container.scale.set(((container as any)._baseScale ?? 1) * 1.05);
      sprite.tint = 0xDDDDDD;
    });
    container.on('pointerout', resetBtn);
    container.on('pointerup', resetBtn);
    container.on('pointerupoutside', resetBtn);
    container.on('pointercancel', resetBtn);

    return container;
  }

  public show(options: EndingScreenOptions): void {
    const interactBtn = document.getElementById('interactionBtn');
    if (interactBtn) {
      interactBtn.style.display = 'none';
      interactBtn.classList.remove('visible');
    }

    // Hide joystick overlay element
    const mobileControls = document.getElementById('mobileControls');
    if (mobileControls) {
      mobileControls.style.display = 'none';
    }

    // Resolve ending name from either endingName or legacy title field
    const endingName =
      options.endingName ??
      options.title ??
      (options.type === 'victory' ? '[Good]' : '[Bad]');

    // Build caption: prefer explicit caption, otherwise use the standard formula
    const captionText =
      options.caption !== undefined
        ? options.caption
        : `You Have Reached Ending ${endingName}.`;

    this.captionText.text = captionText;

    const progress = options.progress !== undefined ? options.progress : 0;
    this.progressValue.text = `${progress}%`;


    this.galleryButton.removeAllListeners('pointertap');
    this.restartButton.removeAllListeners('pointertap');
    this.shareButton.removeAllListeners('pointertap');

    this.galleryButton.on('pointertap', () => options.onGallery?.());
    this.restartButton.on('pointertap', () => options.onRestart?.());
    this.shareButton.on('pointertap', () => options.onShare?.());

    this.updateLayout();
    this.isVisible = true;
    this.container.visible = true;
    this.container.alpha = 0;

    // Fade in
    let alpha = 0;
    const fade = () => {
      alpha += 0.05;
      this.container.alpha = alpha;
      if (alpha < 1) requestAnimationFrame(fade);
    };
    fade();
  }

  public hide(): void {
    this.isVisible = false;
    this.container.visible = false;

    const interactBtn = document.getElementById('interactionBtn');
    if (interactBtn) {
      interactBtn.style.display = '';
    }

    const mobileControls = document.getElementById('mobileControls');
    if (mobileControls) {
      mobileControls.style.display = '';
    }
  }

  private updateLayout(): void {
    const cx = this.screenWidth / 2;
    const cy = this.screenHeight / 2;
    const s = this.getScaleFactor();

    // Background covers screen
    // Scale bg to cover
    if (this.backgroundSprite.texture.width > 1) {
      const bgRatio = this.backgroundSprite.texture.width / this.backgroundSprite.texture.height;
      const screenRatio = this.screenWidth / this.screenHeight;
      const scale = screenRatio > bgRatio
        ? this.screenWidth / this.backgroundSprite.texture.width
        : this.screenHeight / this.backgroundSprite.texture.height;
      this.backgroundSprite.scale.set(scale);
    }
    this.backgroundSprite.position.set(cx, cy);

    // Title
    const titleFontSize = Math.round(100 * s);
    (this.titleText.style as PIXI.TextStyle).fontSize = titleFontSize;
    this.titleText.position.set(cx, this.screenHeight * 0.22);

    //Caption
    const captionFontSize = Math.round(32 * s);
    (this.captionText.style as PIXI.TextStyle).fontSize = captionFontSize;
    // Wrap width: leave 10% margin each side
    (this.captionText.style as PIXI.TextStyle).wordWrapWidth = this.screenWidth * 0.8;
    this.captionText.position.set(cx, this.titleText.y + titleFontSize * 0.8);

    // Progress
    const progressFontSize = Math.round(48 * s);
    (this.progressLabel.style as PIXI.TextStyle).fontSize = progressFontSize;
    (this.progressValue.style as PIXI.TextStyle).fontSize = progressFontSize;

    const labelWidth = this.progressLabel.width;
    const valueWidth = this.progressValue.width;
    const totalProgressWidth = labelWidth + valueWidth;

    this.progressLabel.x = -totalProgressWidth / 2;
    this.progressLabel.y = 0;
    
    // Position value right after label
    this.progressValue.x = this.progressLabel.x + labelWidth;
    this.progressValue.y = 0;

    // Place progress between caption and buttons
    const progressY = this.captionText.y + captionFontSize + Math.round(20 * s);
    this.progressContainer.position.set(cx, progressY);

    // Buttons
    const buttonScale = Math.max(0.25, 0.4 * s);
    const normalizedScale = buttonScale / 0.4; // 0.4 was the original sprite scale

    // Apply scale uniformly to all buttons
    [this.galleryButton, this.restartButton, this.shareButton].forEach(btn => {
      (btn as any)._baseScale = normalizedScale;
      btn.scale.set(normalizedScale);
    });

    const buttonY = this.screenHeight * 0.82;
    this.buttonContainer.position.set(cx, buttonY);

    const btnSpacingH = Math.min(Math.round(this.screenWidth * 0.28), Math.round(250 * s));
    this.galleryButton.x = -btnSpacingH;
    this.galleryButton.y = 0;
    this.restartButton.x = 0;
    this.restartButton.y = 0;
    this.shareButton.x = btnSpacingH;
    this.shareButton.y = 0;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.updateLayout();
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  public destroy(): void {
    this.container.destroy({ children: true });
  }
}
